import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import FormField from "@/components/ui/FormField";
import { Select } from "@/components/ui/Select";
import { FileUpload } from "@/components/ui/FileUpload";
import { Checkbox } from "@/components/ui/Checkbox";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/cn";
import {
  ApplicationFormValues,
  O_LEVEL_TYPE_OPTIONS,
  QUALIFICATION_OPTIONS,
} from "@/pages/landing/application/applicationFormSchema";

interface EducationalInformationFormProps {
  onCertificateSelect: (files: FileList | null) => void;
  certificateFileName?: string;
  certificateError?: string;
  existingCertificateUrl?: string;
  existingCertificateName?: string;
  onResultSelect: (files: FileList | null) => void;
  resultFileName?: string;
  resultError?: string;
  existingResultUrl?: string;
  existingResultName?: string;
  onJambResultSelect: (files: FileList | null) => void;
  jambResultFileName?: string;
  jambResultError?: string;
  existingJambResultUrl?: string;
  existingJambResultName?: string;
}

const EducationalInformationForm: React.FC<EducationalInformationFormProps> = ({
  onCertificateSelect,
  certificateFileName,
  certificateError,
  existingCertificateUrl,
  existingCertificateName,
  onResultSelect,
  resultFileName,
  resultError,
  existingResultUrl,
  existingResultName,
  onJambResultSelect,
  jambResultFileName,
  jambResultError,
  existingJambResultUrl,
  existingJambResultName,
}) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<ApplicationFormValues>();

  useEffect(() => {
    register("education.oLevelTypes");
  }, [register]);

  const educationErrors = errors.education ?? {};
  const selectedOLevelTypes = watch("education.oLevelTypes") ?? [];
  const wroteJamb = watch("education.wroteJamb");

  const toggleOLevelType = (type: string) => {
    const current = selectedOLevelTypes as string[];
    const next = current.includes(type)
      ? current.filter((item) => item !== type)
      : [...current, type];
    setValue("education.oLevelTypes", next, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <div className="space-y-10">
      <div className="grid md:grid-cols-2 gap-6">
        <FormField label="Highest academic qualification" required>
          <Select
            defaultValue=""
            {...register("education.highestQualification")}
            className={cn(
              educationErrors.highestQualification &&
                "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
            )}
          >
            <option value="" disabled>
              Select qualification
            </option>
            {QUALIFICATION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
          {educationErrors.highestQualification && (
            <p className="text-xs text-red-600 mt-1">
              {educationErrors.highestQualification.message as string}
            </p>
          )}
        </FormField>

        <FormField label="Have an O’Level Certificate?" required>
          <Select
            defaultValue=""
            {...register("education.hasOLevel")}
            className={cn(
              educationErrors.hasOLevel &&
                "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
            )}
          >
            <option value="" disabled>
              Select option
            </option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
          {educationErrors.hasOLevel && (
            <p className="text-xs text-red-600 mt-1">
              {educationErrors.hasOLevel.message as string}
            </p>
          )}
        </FormField>

        <FormField label="Number of O'Level exam sittings" required>
          <Input
            type="number"
            min={1}
            {...register("education.oLevelTimes")}
            className={cn(
              educationErrors.oLevelTimes &&
                "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
            )}
          />
          {educationErrors.oLevelTimes && (
            <p className="text-xs text-red-600 mt-1">
              {educationErrors.oLevelTimes.message as string}
            </p>
          )}
        </FormField>

        <FormField label="O'Level certificate type" required>
          <div className="flex flex-wrap gap-4 mt-2">
            {O_LEVEL_TYPE_OPTIONS.map((option) => (
              <Checkbox
                key={option}
                label={option}
                checked={selectedOLevelTypes.includes(option)}
                onChange={() => toggleOLevelType(option)}
              />
            ))}
          </div>
          {educationErrors.oLevelTypes && (
            <p className="text-xs text-red-600 mt-1">
              {educationErrors.oLevelTypes.message as string}
            </p>
          )}
        </FormField>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <FormField label="">
          <FileUpload
            label="Upload your Certificate"
            helperText={
              <div className="space-y-1">
                <p>
                  Original degree certificate or postgraduate diploma. Upload in
                  PDF or image format.
                </p>
                {certificateFileName && (
                  <p className="text-xs text-slate-500">
                    Selected: {certificateFileName}
                  </p>
                )}
                {certificateError && (
                  <p className="text-xs text-red-600">{certificateError}</p>
                )}
                {existingCertificateUrl && (
                  <p className="text-xs text-slate-500">
                    Previously uploaded:{" "}
                    <a
                      href={existingCertificateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#7b2a40] underline"
                    >
                      {existingCertificateName ?? "View document"}
                    </a>
                  </p>
                )}
              </div>
            }
            accept=".pdf,.jpg,.jpeg,.png"
            onFileSelect={onCertificateSelect}
          />
        </FormField>

        <FormField label="">
          <FileUpload
            label="Upload your O’Level Result"
            helperText={
              <div className="space-y-1">
                <p>
                  Upload original copy of your O’Level result in PDF or image
                  format.
                </p>
                {resultFileName && (
                  <p className="text-xs text-slate-500">
                    Selected: {resultFileName}
                  </p>
                )}
                {resultError && (
                  <p className="text-xs text-red-600">{resultError}</p>
                )}
                {existingResultUrl && (
                  <p className="text-xs text-slate-500">
                    Previously uploaded:{" "}
                    <a
                      href={existingResultUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#7b2a40] underline"
                    >
                      {existingResultName ?? "View document"}
                    </a>
                  </p>
                )}
              </div>
            }
            accept=".pdf,.jpg,.jpeg,.png"
            onFileSelect={onResultSelect}
          />
        </FormField>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <FormField label="Wrote JAMB exams?" required>
          <Select
            defaultValue=""
            {...register("education.wroteJamb")}
            className={cn(
              educationErrors.wroteJamb &&
                "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
            )}
          >
            <option value="" disabled>
              Select option
            </option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
          {educationErrors.wroteJamb && (
            <p className="text-xs text-red-600 mt-1">
              {educationErrors.wroteJamb.message as string}
            </p>
          )}
        </FormField>

        {wroteJamb === "yes" && (
          <FormField label="JAMB Score" required>
            <Input
              type="number"
              min={1}
              {...register("education.jambScore")}
              className={cn(
                educationErrors.jambScore &&
                  "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
              )}
            />
            {educationErrors.jambScore && (
              <p className="text-xs text-red-600 mt-1">
                {educationErrors.jambScore.message as string}
              </p>
            )}
          </FormField>
        )}

        <FormField label="">
          <FileUpload
            label="Upload your JAMB Result"
            helperText={
              <div className="space-y-1">
                <p>Upload in PDF or image format.</p>
                {jambResultFileName && (
                  <p className="text-xs text-slate-500">
                    Selected: {jambResultFileName}
                  </p>
                )}
                {jambResultError && (
                  <p className="text-xs text-red-600">{jambResultError}</p>
                )}
                {existingJambResultUrl && (
                  <p className="text-xs text-slate-500">
                    Previously uploaded:{" "}
                    <a
                      href={existingJambResultUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#7b2a40] underline"
                    >
                      {existingJambResultName ?? "View document"}
                    </a>
                  </p>
                )}
              </div>
            }
            accept=".pdf,.jpg,.jpeg,.png"
            onFileSelect={onJambResultSelect}
          />
        </FormField>
      </div>
    </div>
  );
};

export default EducationalInformationForm;
